import useApiHelpers from '@/composables/api/helpers';
import { useGlobalStore } from '@/stores/globalStore';
import { deSaturateColor } from '@/composables/ui/useColors';
import useGoogleFonts from '@/composables/ui/useGoogleFonts';
import { THEME_DARK, THEME_LIGHT } from '@/constants/theme';

const useTheme = () => {
  const { getRequest } = useApiHelpers();
  const stripHash = (str) => (str && str[0] === '#' ? str.substring(1) : str);

  const updateTheme = (useLightTheme) => {
    const rootEl = document.documentElement;
    const rootUsingLightTheme = rootEl.classList.contains('lightmode');

    if (useLightTheme) {
      if (!rootUsingLightTheme) {
        rootEl.classList.add('lightmode');
      }
    } else if (rootUsingLightTheme) {
      rootEl.classList.remove('lightmode');
    }
  };

  const setThemeColours = (theme, setBackground = false) => {
    const root = document.documentElement;

    const {
      primary = null, secondary = null, background = null, textColor = null,
    } = theme || {};

    // Extract border_radius (snake_case from API) or borderRadius (camelCase)
    const borderRadius = theme?.border_radius ?? theme?.borderRadius ?? null;

    if (primary) {
      root.style.setProperty('--primary', `${primary}`);
    }
    if (secondary) {
      root.style.setProperty('--secondary', `${secondary}`);
    }
    if (background && setBackground) {
      root.style.setProperty('--background', `${background}`);
      root.style.setProperty('--settings-overlay-background', `${background}`);
      root.style.setProperty('--dropdown-background', `${background}`);
      // if background is set, disable floating background gradients
      root.style.setProperty('--backgroundGradientOpacity', '0.0');
    }
    if (textColor) {
      root.style.setProperty('--text-color', textColor);
      root.style.setProperty('--footer-text-color', textColor);
      root.style.setProperty('--input-place-holder-text-color', textColor);
    }
    if (borderRadius != null && borderRadius !== '') {
      // Convert to string and trim whitespace
      const borderRadiusStr = String(borderRadius).trim();

      // Skip if empty after trimming
      if (borderRadiusStr) {
        // Validate and format border radius value
        const isValidCssLength = /^[\d.]+(rem|px|em|%|ch|ex|vh|vw|vmin|vmax|cm|mm|in|pt|pc)$/i.test(borderRadiusStr);
        const isNumeric = /^[\d.]+$/.test(borderRadiusStr);

        let borderRadiusValue = borderRadiusStr;

        // If it's just a number, assume rem units
        if (isNumeric && !isValidCssLength) {
          borderRadiusValue = `${borderRadiusStr}rem`;
        } else if (!isValidCssLength) {
          // Invalid format, fallback to default
          borderRadiusValue = '1.6rem';
        }

        root.style.setProperty('--border-radius', borderRadiusValue);
        // Debug: uncomment to verify border radius is being set
        // console.log('Border radius set to:', borderRadiusValue);
      }
    }

    // Extract font_family (snake_case) or fontFamily (camelCase)
    const fontFamily = theme?.font_family ?? theme?.fontFamily ?? null;
    const fontWeights = theme?.font_weights ?? theme?.fontWeights ?? ['400', '500', '700'];

    const { loadAndApplyFont } = useGoogleFonts();

    if (fontFamily && fontFamily.trim() !== '') {
      // Load and apply Google Font
      loadAndApplyFont(fontFamily, fontWeights);
    } else {
      // Reset to default Rubik font
      root.style.setProperty('--font-family', "'Rubik', Arial, Helvetica, sans-serif");
      root.style.setProperty('--font-family-variable', "'Rubik-Variable', Arial, Helvetica, sans-serif");
    }
  };

  /**
   * Detects the user's system theme preference using prefers-color-scheme media query
   * @returns {string} 'DARK' if system prefers dark mode,
   * 'LIGHT' if system prefers light mode, 'DARK' as fallback
   */
  const detectSystemTheme = () => {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return THEME_DARK;
    }

    try {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      return prefersDark ? THEME_DARK : THEME_LIGHT;
    } catch (error) {
      // Fallback to dark mode if media query is not supported
      return THEME_DARK;
    }
  };

  const themeIsFromUrl = async (query) => {
    const merchantColorsFromStorage = JSON.parse(
      sessionStorage.getItem('merchantColors'),
    );
    const isThemeFromUrl = merchantColorsFromStorage?.themeSet === 'url';

    const isThemeQuery = Promise.allSettled([query]).then((res) => {
      const [themeQuery] = res;
      const searchValues = [
        'primaryColor',
        'secondaryColor',
        'backgroundColor',
        'textColor',
      ];

      if (!themeQuery) {
        return false;
      }

      const { searchParams } = new URL(document.location);
      const searchParamsHasThemeFromUrl = themeQuery.value
        ? Object.keys(themeQuery.value).some((key) => searchValues.includes(key))
        : searchValues.some((value) => searchParams.has(value));

      return (
        (searchParamsHasThemeFromUrl && searchParams.size > 0)
        || isThemeFromUrl
      );
    });

    return isThemeQuery;
  };

  const updateThemeFromUrl = () => {
    const store = useGlobalStore();

    const { searchParams } = new URL(window.location.href);
    const merchantColorsFromStorage = JSON.parse(
      sessionStorage.getItem('merchantColors'),
    );

    const primary = searchParams.get('primaryColor')
      || merchantColorsFromStorage?.primary
      || '4b9cdd';

    const secondary = searchParams.get('secondaryColor')
      || merchantColorsFromStorage?.secondary
      || '00d6bf';

    // if a background has not been set default to store theme
    const background = searchParams.get('backgroundColor')
      || merchantColorsFromStorage?.background
      || '111630';
    const updateBackgroundColor = !!background;

    // defaults to white text
    const textColor = searchParams.get('textColor')
      || merchantColorsFromStorage?.textColor
      || 'ffffff';

    // if no background has been set then default to banxa theme
    if (!updateBackgroundColor) {
      const useLightTheme = store.getIsLightMode;
      updateTheme(useLightTheme);
    }

    setThemeColours(
      {
        primary: `#${primary}`,
        secondary: `#${secondary}`,
        background: `#${background}`,
        textColor: `#${textColor}`,
      },
      updateBackgroundColor,
    );

    sessionStorage.setItem(
      'merchantColors',
      JSON.stringify({
        primary: stripHash(primary),
        secondary: stripHash(secondary),
        background: stripHash(background),
        textColor: stripHash(textColor),
        themeSet: 'url',
      }),
    );
  };

  /**
* Adds/removes 'lightmode' class to the root html element
* depending on boolean value light theme in pinia store.
   */
  const updateThemeFromStore = () => {
    const store = useGlobalStore();
    // If theme is not set, detect system preference and set it
    if (store.global.theme === null) {
      const systemTheme = detectSystemTheme();
      store.setTheme(systemTheme);
    }

    const useLightTheme = store.getIsLightMode;
    const merchantTheme = store.global?.merchant?.theme;

    updateTheme(useLightTheme);

    if (merchantTheme) {
      const setBackground = store.getIsBanxaNative || merchantTheme?.background;
      // Extract border_radius (snake_case from API) or borderRadius (camelCase)
      const borderRadius = merchantTheme?.border_radius || merchantTheme?.borderRadius;
      setThemeColours({ ...merchantTheme, borderRadius }, setBackground);
    }
  };

  /**
   * Validates theme query, updates the pinia store with the value used in query, updates the theme.
   * @param {string} queryVal value passed in for 'theme' in the route
   */
  const updateThemeFromQuery = (queryVal) => {
    const globalStore = useGlobalStore();

    const isLightmodeRequest = ['lightmode', 'light'].includes(
      queryVal.toLowerCase(),
    );
    const theme = isLightmodeRequest ? THEME_LIGHT : THEME_DARK;
    globalStore.setTheme(theme);

    updateThemeFromStore();
  };

  const updateAppTheme = (query) => {
    if (!query) {
      return updateThemeFromStore();
    }

    return themeIsFromUrl(query).then((isThemeQuery) => {
      if (!isThemeQuery) {
        return updateThemeFromStore();
      }

      return updateThemeFromUrl();
    });
  };

  const updateThemeFromMerchant = (theme) => {
    const globalStore = useGlobalStore();
    const themeCopy = { ...theme };
    globalStore.setMerchantSettings({ theme: themeCopy });

    if (globalStore.getIsBanxaNative) {
      const primaryColor = themeCopy?.primary || '#4b9cdd';
      themeCopy.secondary = deSaturateColor(primaryColor, 0.2);
    }

    const {
      primary,
      secondary,
      background,
    } = themeCopy;

    sessionStorage.setItem(
      'merchantColors',
      JSON.stringify({
        primary: stripHash(primary),
        secondary: stripHash(secondary),
        background: stripHash(background),
        themeSet: 'merchapi',
      }),
    );

    // we set a background on Banxa Native when there are merch theme background present
    // or if just background is present
    const setBg = (globalStore.getIsBanxaNative && background) || background;
    let themeMode;

    setThemeColours(themeCopy, setBg);

    // don't override the setting when the theme is set
    // Prioritise the API or referralURL before sessionStorage
    if (globalStore.getIsThemeSet && !theme?.mode) {
      return;
    }

    if (theme?.mode) {
      // Use theme mode from API (normalise to uppercase)
      themeMode = theme.mode.toUpperCase() === 'LIGHT' ? THEME_LIGHT : THEME_DARK;
    } else if (globalStore.global.theme === null) {
      // No API mode and no existing theme - detect system preference
      themeMode = detectSystemTheme();
    } else {
      // No API mode but theme already set - keep existing theme
      themeMode = globalStore.global.theme;
    }

    globalStore.setTheme(themeMode);
    updateTheme(themeMode === THEME_LIGHT);
  };

  const getMerchTheme = () => new Promise((resolve, reject) => {
    getRequest('/theme').then((res) => {
      updateThemeFromMerchant(res.data);
      resolve(res);
    }).catch((error) => {
      reject(error);
    });
  });

  return {
    updateThemeFromStore,
    updateThemeFromQuery,
    updateThemeFromMerchant,
    updateAppTheme,
    themeIsFromUrl,
    getMerchTheme,
    setThemeColours,
    updateTheme,
  };
};

export default useTheme;
